/**
 * /app/$tenantSlug/attendance/regularize — Regularize (self).
 *
 * Lists the caller's recent day rows and lets them file a correction
 * (missed punch / wrong time / WFH / on-duty) for a date. A pending
 * regularization for a date blocks a second (409 pending_request_exists).
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useMyDays,
  useRegularizations,
  useCreateRegularization,
  useCancelRegularization,
  useApproveRegularization,
} from "@/hooks/use-attendance";
import { SoleApproverConfirm } from "@/components/approvals/sole-approver-confirm";
import { regReasonLabel, type RegReasonType } from "@/types/attendance";
import { toApiError } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { decidedByLabel } from "@/lib/approval-copy";
import { useDirectory } from "@/hooks/use-directory";
import { parseDayParam } from "@/lib/attendance-register";

// ?date=YYYY-MM-DD starts the correction on that day (My Time's
// missing-day card, "I forgot to punch").
const searchSchema = z.object({
  date: z.string().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/regularize",
)({
  validateSearch: searchSchema,
  component: RegularizeRoute,
});

const REASONS: { value: RegReasonType; label: string }[] = [
  { value: "missed_punch", label: "Missed Punch" },
  { value: "wrong_time", label: "Wrong Time" },
  { value: "wfh", label: "Work from Home" },
  { value: "on_duty", label: "On Duty" },
  { value: "other", label: "Other" },
];

function RegularizeRoute() {
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  }, []);
  const days = useMyDays(range.from, range.to);
  const regs = useRegularizations(undefined, "self");
  const create = useCreateRegularization();
  const cancel = useCancelRegularization();
  const approveOwn = useApproveRegularization();
  const dir = useDirectory({ includeInactive: true });

  const search = Route.useSearch();
  const [workDate, setWorkDate] = useState(() => parseDayParam(search.date) ?? "");
  const [reason, setReason] = useState<RegReasonType>("missed_punch");
  const [inAt, setInAt] = useState("");
  const [outAt, setOutAt] = useState("");
  const [note, setNote] = useState("");

  async function submit() {
    if (!workDate || !note.trim()) {
      toast.error("Date and reason note are required");
      return;
    }
    try {
      await create.mutateAsync({
        work_date: workDate,
        reason_type: reason,
        requested_in_at: inAt ? new Date(inAt).toISOString() : null,
        requested_out_at: outAt ? new Date(outAt).toISOString() : null,
        reason_note: note.trim(),
      });
      toast.success("Regularization submitted");
      setWorkDate("");
      setInAt("");
      setOutAt("");
      setNote("");
    } catch (e) {
      const err = toApiError(e);
      toast.error(
        err.error === "pending_request_exists"
          ? "A pending request already exists for that date"
          : (err.message ?? "Failed to submit"),
      );
    }
  }

  return (
    <TimeAndLeaveShell
      title="Regularize"
      description="Fix a missed or wrong punch. Your manager or HR will review it."
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>New Correction</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="wd">Date</Label>
              <Input
                id="wd"
                type="date"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Select value={reason} onValueChange={(v) => setReason(v as RegReasonType)}>
                <SelectTrigger aria-label="Reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="in">Corrected in (Optional)</Label>
              <Input
                id="in"
                type="datetime-local"
                value={inAt}
                onChange={(e) => setInAt(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="out">Corrected Out (Optional)</Label>
              <Input
                id="out"
                type="datetime-local"
                value={outAt}
                onChange={(e) => setOutAt(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="note">Reason Note</Label>
            <Textarea
              id="note"
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explain what happened"
            />
          </div>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? "Submitting…" : "Submit Regularization"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>My Recent Days</CardTitle>
        </CardHeader>
        <CardContent>
          {days.data && days.data.days.length > 0 ? (
            <ul className="divide-y text-sm">
              {days.data.days.map((d) => (
                <li key={d.work_date} className="flex items-center justify-between py-2">
                  <span>{formatDate(d.work_date)}</span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={d.status} />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setWorkDate(d.work_date)}
                    >
                      Fix This Day
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No recent days." />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>My Regularizations</CardTitle>
        </CardHeader>
        <CardContent>
          {regs.data && regs.data.regularizations.length > 0 ? (
            <ul className="divide-y text-sm">
              {regs.data.regularizations.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 py-2">
                  <span>
                    {formatDate(r.work_date)} — {regReasonLabel(r.reason_type)}
                    {/* Who decided it — the sole approver's audited
                        self-approval reads "(self-approved)" (2026-10
                        re-test), and a rejection carries its reason. */}
                    {r.decided_by_id != null &&
                      (r.status === "approved" || r.status === "rejected") && (
                        <span className="block text-xs text-muted-foreground">
                          {r.status === "approved" ? "Approved" : "Rejected"} by{" "}
                          {decidedByLabel(
                            dir.label(r.decided_by_id, null, false),
                            r.decided_by_id,
                            r.employee_id,
                          )}
                          {r.decided_at ? ` on ${formatDateTime(r.decided_at)}` : ""}
                          {r.status === "rejected" && r.decision_notes
                            ? ` — ${r.decision_notes}`
                            : ""}
                        </span>
                      )}
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    {/* The workspace's only approver may approve their own
                        correction — explicitly, audited (2026-10 QA #1). */}
                    {r.status === "pending" && r.can_self_approve && (
                      <SoleApproverConfirm
                        noun="attendance correction"
                        consequence="The corrected punches are written to the day and count for payroll."
                        onApprove={() =>
                          approveOwn.mutateAsync({ id: r.id, body: { self_approve: true } })
                        }
                      />
                    )}
                    {r.status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancel.mutate(r.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No requests yet." />
          )}
        </CardContent>
      </Card>
      </div>
    </TimeAndLeaveShell>
  );
}
